import ListPagination from '../components/common/ListPagination';
import useListPagination from '../hooks/useListPagination';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  HomeIcon,
  CalendarIcon,
  DocumentTextIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Modal from '../components/ui/Modal';
import Dropdown from '../components/ui/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import WFHService from '../services/wfh.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const WFHRequest = () => {
  const { user, isAdmin, isHR, isManager } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const [activeTab, setActiveTab] = useState('requests');
  const [wfhRequests, setWfhRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [formData, setFormData] = useState({
    date: '',
    reason: '',
    description: ''
  });

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchWfhRequests();
    } else if (activeTab === 'approvals' && (isAdmin || isHR || isManager)) {
      fetchPendingApprovals();
    }
  }, [activeTab, isAdmin, isHR, isManager]);

  const fetchWfhRequests = async () => {
    try {
      setLoading(true);
      const response = await WFHService.getMyRequests();
      setWfhRequests(response || []);
    } catch (error) {
      console.error('Error fetching WFH requests:', error);
      toast.error('Failed to load WFH requests');
      setWfhRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      setApprovalsLoading(true);
      const response = await WFHService.getAllRequests();
      const requests = Array.isArray(response) ? response : (response?.requests || []);
      const pending = requests.filter(req => req.status === 'PENDING');
      setPendingApprovals(pending);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      toast.error('Failed to load pending approvals');
      setPendingApprovals([]);
    } finally {
      setApprovalsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await WFHService.createRequest(formData);
      toast.success('WFH request submitted successfully');
      setShowForm(false);
      setFormData({ date: '', reason: '', description: '' });
      fetchWfhRequests();
    } catch (error) {
      console.error('Error submitting WFH request:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to submit WFH request');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      setApproving(true);
      await WFHService.updateRequestStatus(requestId, { status: 'approved' });
      toast.success('WFH request approved successfully');
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error approving WFH request:', error);
      toast.error('Failed to approve WFH request');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setApproving(true);
      await WFHService.updateRequestStatus(selectedRequest.id, { 
        status: 'rejected',
        rejectionReason 
      });
      toast.success('WFH request rejected');
      setShowRejectModal(false);
      setRejectionReason('');
      setSelectedRequest(null);
      fetchPendingApprovals();
    } catch (error) {
      console.error('Error rejecting WFH request:', error);
      toast.error('Failed to reject WFH request');
    } finally {
      setApproving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'approved': return CheckCircleIcon;
      case 'rejected': return XCircleIcon;
      default: return ClockIcon;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateDisplay(dateString);
  };

  const reasonOptions = [
    'Personal appointment',
    'Medical appointment',
    'Child care',
    'Home maintenance',
    'Family emergency',
    'Transportation issues',
    'Other'
  ];

  const filterBySearch = (list) => {
    if (!headerSearch) return list;
    const q = headerSearch.toLowerCase();
    return list.filter(r =>
      (r.reason && r.reason.toLowerCase().includes(q)) ||
      (r.description && r.description.toLowerCase().includes(q)) ||
      (r.status && r.status.toLowerCase().includes(q)) ||
      (r.employeeName && r.employeeName.toLowerCase().includes(q)) ||
      (r.date && r.date.includes(q))
    );
  };

  const filteredWfhRequests = filterBySearch(wfhRequests);
  const filteredApprovals = filterBySearch(pendingApprovals);
  const pagination = useListPagination(activeTab === 'requests' ? filteredWfhRequests : filteredApprovals, JSON.stringify([activeTab, headerSearch]));

  if (loading && wfhRequests.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-xl p-6 text-white"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Work From Home 🏠</h1>
            <p className="text-emerald-100">Request and manage your work from home days</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-white bg-opacity-20 text-white border-2 border-white hover:bg-white hover:text-emerald-600 transition-all duration-200"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            New WFH Request
          </Button>
        </div>
      </motion.div>

      {/* Tabs */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <Button
                variant={activeTab === 'requests' ? 'primary' : 'outline'}
                onClick={() => setActiveTab('requests')}
                className="flex items-center space-x-2"
              >
                <DocumentTextIcon className="w-4 h-4" />
                <span>My Requests</span>
                {wfhRequests && wfhRequests.length > 0 && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === 'requests' ? 'bg-white text-emerald-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {wfhRequests.length}
                  </span>
                )}
              </Button>
              
              {(isAdmin || isHR || isManager) && (
                <Button
                  variant={activeTab === 'approvals' ? 'primary' : 'outline'}
                  onClick={() => setActiveTab('approvals')}
                  className="flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Pending Approvals</span>
                  {pendingApprovals && pendingApprovals.length > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      activeTab === 'approvals' ? 'bg-white text-emerald-600' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {pendingApprovals.length}
                    </span>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Quick Stats - Only show on My Requests tab */}
      {activeTab === 'requests' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <Card.Body className="p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {wfhRequests.filter(req => req.status?.toLowerCase() === 'approved').length}
              </h3>
              <p className="text-sm text-gray-600">Approved Requests</p>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className="p-6 text-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <ClockIcon className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {wfhRequests.filter(req => req.status?.toLowerCase() === 'pending').length}
              </h3>
              <p className="text-sm text-gray-600">Pending Requests</p>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <HomeIcon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{wfhRequests.length}</h3>
              <p className="text-sm text-gray-600">Total Requests</p>
            </Card.Body>
          </Card>
        </div>
      )}

      {/* Content based on active tab */}
      <div className="space-y-4">
        <div ref={pagination.topRef}><ListPagination {...pagination} compact /></div>
        {activeTab === 'requests' ? (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Your WFH Requests</h2>
            
            {filteredWfhRequests.length > 0 ? (
              pagination.items.map((request, index) => {
                const StatusIcon = getStatusIcon(request.status);
                return (
                  <motion.div
                    key={request.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow duration-300">
                      <Card.Body className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                              <HomeIcon className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">WFH Request</h3>
                              <p className="text-sm text-gray-600">{request.reason}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <StatusIcon className="w-5 h-5" />
                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(request.status)}`}>
                              {request.status?.charAt(0).toUpperCase() + request.status?.slice(1).toLowerCase()}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-sm text-gray-600">WFH Date</p>
                              <p className="font-medium">{formatDate(request.date)}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <ClockIcon className="w-5 h-5 text-gray-400" />
                            <div>
                              <p className="text-sm text-gray-600">Requested On</p>
                              <p className="font-medium">{formatDate(request.createdAt)}</p>
                            </div>
                          </div>
                        </div>

                        {request.rejectionReason && request.status?.toLowerCase() === 'rejected' && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-red-800">
                              <span className="font-medium">Rejection Reason:</span> {request.rejectionReason}
                            </p>
                          </div>
                        )}

                        {request.status?.toLowerCase() === 'approved' && request.approver && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <p className="text-sm text-green-800">
                              <span className="font-medium">Approved by:</span> {request.approver.first_name} {request.approver.last_name}
                            </p>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </motion.div>
                );
              })
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <HomeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No WFH Requests</h3>
                  <p className="text-gray-600 mb-4">You haven't submitted any work from home requests yet.</p>
                  <Button onClick={() => setShowForm(true)}>
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Submit Your First Request
                  </Button>
                </Card.Body>
              </Card>
            )}
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Pending Approvals</h2>
            
            {approvalsLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-32 bg-gray-200 rounded-xl"></div>
                ))}
              </div>
            ) : filteredApprovals.length > 0 ? (
              pagination.items.map((request, index) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <Card.Body className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                            <UserIcon className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {request.employee ? 
                                `${request.employee.first_name} ${request.employee.last_name}` : 
                                'Employee Name'}
                            </h3>
                            <p className="text-sm text-gray-600">{request.email_address}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className="px-3 py-1 text-sm font-medium rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <CalendarIcon className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">WFH Date</p>
                            <p className="font-medium">{formatDate(request.date)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600">Requested On</p>
                            <p className="font-medium">{formatDate(request.createdAt)}</p>
                          </div>
                        </div>
                      </div>

                      {request.reason && (
                        <div className="mb-4 flex items-start space-x-2">
                          <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                          <div>
                            <p className="text-sm text-gray-600">Reason</p>
                            <p className="text-gray-900">{request.reason}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex space-x-3 pt-4 border-t border-gray-200">
                        <Button
                          variant="success"
                          onClick={() => handleApprove(request.id)}
                          className="flex items-center space-x-2"
                          loading={approving}
                          disabled={approving}
                        >
                          <CheckCircleIcon className="w-4 h-4" />
                          <span>Approve</span>
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowRejectModal(true);
                          }}
                          className="flex items-center space-x-2"
                          disabled={approving}
                        >
                          <XCircleIcon className="w-4 h-4" />
                          <span>Reject</span>
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              ))
            ) : (
              <Card>
                <Card.Body className="p-12 text-center">
                  <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">All Caught Up!</h3>
                  <p className="text-gray-600">There are no pending WFH requests to review.</p>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>

      <ListPagination {...pagination} label="WFH requests" disabled={loading || approvalsLoading || approving} />

      {/* WFH Request Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setFormData({ date: '', reason: '', description: '' });
        }}
        title="Request Work From Home"
      >
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                WFH Date *
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason *
              </label>
              <Dropdown
                value={formData.reason}
                onChange={(value) => handleInputChange({ target: { name: 'reason', value } })}
                options={reasonOptions.map(reason => ({ value: reason, label: reason }))}
                placeholder="Select reason"
                className="w-full"
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <Button type="submit" className="flex-1">
                Submit Request
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setFormData({ date: '', reason: '', description: '' });
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectionReason('');
          setSelectedRequest(null);
        }}
        title="Reject WFH Request"
      >
        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              You are about to reject the WFH request from:
            </p>
            <p className="font-medium text-gray-900">
              {selectedRequest?.employee ? 
                `${selectedRequest.employee.first_name} ${selectedRequest.employee.last_name}` : 
                selectedRequest?.email_address}
            </p>
            <p className="text-sm text-gray-600">
              Date: {formatDate(selectedRequest?.date)}
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Rejection *
            </label>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows="4"
              placeholder="Please provide a reason for rejecting this request..."
              required
            />
          </div>

          <div className="flex space-x-3">
            <Button
              variant="danger"
              onClick={handleReject}
              className="flex-1"
              loading={approving}
              disabled={approving || !rejectionReason.trim()}
            >
              Confirm Rejection
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowRejectModal(false);
                setRejectionReason('');
                setSelectedRequest(null);
              }}
              className="flex-1"
              disabled={approving}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WFHRequest;
